/* eslint-disable array-callback-return */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  FormControl,
  Checkbox,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { withStyles } from '@material-ui/core';
import React, { useState, useMemo } from 'react';
import Spinner from 'modules/common/spinner.component';
import AddressAutocompleteSection from 'modules/authorizations/details/addressManagementCrudTable.component';
import { compose } from 'recompose';
import { getDependentOptions, setValueAndClearDependents } from 'modules/common/utils';

const styles = theme => ({
  editModalContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100% !important',
    marginLeft: '0px !important',
  },
  editModalColumn: {
    display: 'flex',
    flexDirection: 'column',
    width: '100% !important',
  },
  editModalInput: {
    minWidth: '332px',
  },
  dialogContentContainer: {
    margin: '15px',
  }
});

const chunkColumns = (columns, chunkSize) => {
  const rows = [];
  for (let i = 0; i < columns.length; i += chunkSize) {
    rows.push(columns.slice(i, i + chunkSize));
  }
  return rows;
};

// Edit Modal for Vendor Contacts with Google Places Integration
const ContactsEditRecordModal = ({ open, classes, columns, onClose, row, onSubmit, hideEdit, isLoading, countriesEnum, provincesEnum }) => {
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = row.getValue(column.accessorKey);
      return acc;
    }, {}),
  );

  const [originalValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = row.getValue(column.accessorKey);
      return acc;
    }, {}),
  );

  // Check if form is dirty (has changes)
  const isDirty = useMemo(() => {
    return Object.keys(values).some(key => values[key] !== originalValues[key]);
  }, [values, originalValues]);

  const renderInput = (column) => {
    if (column?.hideInModel === true || (column.shouldHideField != null && column.shouldHideField(values))) {
      return null;
    }
    if (column.type === 'text') {
      return (
        <div>
          <TextField
            key={column.accessorKey}
            label={column.header}
            name={column.accessorKey}
            required={column.required}
            value={values[column.accessorKey] || ''}
            disabled={column.accessorKey === 'fieldId' ? values[column.accessorKey]?.length > 0 : column.disabled}
            margin="dense"
            variant="standard"
            sx={{ width: '360px' }}
            onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
          />
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if (column.type === 'dropdown') {
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              variant="standard"
              required={column.required}
              sx={{ width: '360px' }}
              value={values[column.accessorKey] || ''}
              onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
            >
              {column.options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if(column.type === 'checkbox') {
            return (
              <div>
                <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                  <FormControlLabel
                    key={column.accessorKey}
                    name={column.accessorKey}
                    label={column.header}
                    labelPlacement="start"
                    control={<Checkbox />}
                    required={column.required}
                    disabled={column.disabled}
                    checked={values[column.accessorKey]}
                    variant="standard"
                    margin="dense"
                    sx={{ marginTop: '6px', minWidth: '180px', maxWidth: '360px' }}
                    onClick={e => setValues({ ...values, [e.target.name]: e.target.checked })}
                  />
                </FormControl>
                {column.required && (
                  <>
                    <Typography variant="caption" display="block" color="primary">
                      Required
                    </Typography>
                  </>
                )}
              </div>
            );
    }
    if (column.type === 'dropdown-v2') {
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              variant="standard"
              required={column.required}
              disabled={column.disabled || (!!column.parentField && !values[column.parentField])}
              sx={{ width: '360px' }}
              value={values[column.accessorKey] || ''}
              onChange={(e) => setValues(setValueAndClearDependents(columns, values, e.target.name, e.target.value))}
            >
              {getDependentOptions(column, values).map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    return null;
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(values, row);
  };

  const chunkedColumns = chunkColumns(columns, 3);

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <Box sx={{ padding: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Edit Contact
        </Typography>
        
        {/* Google Places Address Search Section */}
        <AddressAutocompleteSection
          values={values}
          setValues={setValues}
          countriesEnum={countriesEnum}
          provincesEnum={provincesEnum}
        />

        {/* Manual Input Fields */}
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem',
            }}
          >
            {chunkedColumns.map((columnChunk, rowIndex) => (
                <Grid container item spacing={2} key={rowIndex}>
                  {columnChunk.map((column, colIndex) => {
                    if(column.dependentOn && values[column.dependentOn] === column.dependentValue)
                    {
                      return (
                        <Grid item xs={4} key={column.accessorKey}>
                          {renderInput(column, rowIndex * colIndex)}
                        </Grid>
                      );
                    }
                    else {
                      return (
                        <Grid item xs={4} key={column.accessorKey}>
                          {renderInput(column, rowIndex * colIndex)}
                        </Grid>
                      );
                    }
                  })}
                </Grid>
              ))}
          </Stack>
        </div>
      </Box>
      {isLoading && (
        <DialogActions sx={{ p: '1.25rem' }}>
          <Spinner />
        </DialogActions>
      )}
      {!isLoading && (
        <DialogActions sx={{ p: '1.25rem' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} variant="contained" disabled={!isDirty}>
            Save
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

// Create Modal for Vendor Contacts with Google Places Integration
const ContactsCreateRecordModal = ({ open, classes, columns, onClose, onSubmit, hideEdit, isLoading, countriesEnum, provincesEnum }) => {
  const [values, setValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {}),
  );

  const [originalValues] = useState(() =>
    columns.reduce((acc, column) => {
      acc[column.accessorKey ?? ''] = '';
      return acc;
    }, {}),
  );

  // Check if form is dirty (has changes)
  const isDirty = useMemo(() => {
    return Object.keys(values).some(key => values[key] !== originalValues[key]);
  }, [values, originalValues]);

  const renderInput = (column) => {
    if (column?.hideInModel === true || (column.shouldHideField != null && column.shouldHideField(values))) {
      return null;
    }
    if (column.type === 'text') {
      return (
        <div>
          <TextField
            key={column.accessorKey}
            label={column.header}
            name={column.accessorKey}
            required={column.required}
            value={values[column.accessorKey] || ''}
            disabled={column.disabled}
            margin="dense"
            variant="standard"
            sx={{ width: '360px' }}
            onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
          />
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if (column.type === 'dropdown') {
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              variant="standard"
              required={column.required}
              sx={{ width: '360px' }}
              value={values[column.accessorKey] || ''}
              onChange={(e) => setValues({ ...values, [e.target.name]: e.target.value })}
            >
              {column.options.map((opt) => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    if(column.type === 'checkbox') {
            return (
              <div>
                <FormControl sx={{ marginTop: '20px', marginBottom: '1px' }}>
                  <FormControlLabel
                    key={column.accessorKey}
                    name={column.accessorKey}
                    label={column.header}
                    labelPlacement="start"
                    control={<Checkbox />}
                    required={column.required}
                    disabled={column.disabled}
                    checked={values[column.accessorKey]}
                    variant="standard"
                    margin="dense"
                    sx={{ marginTop: '6px', minWidth: '180px', maxWidth: '360px' }}
                    onClick={e => setValues({ ...values, [e.target.name]: e.target.checked })}
                  />
                </FormControl>
                {column.required && (
                  <>
                    <Typography variant="caption" display="block" color="primary">
                      Required
                    </Typography>
                  </>
                )}
              </div>
            );
          }
    if (column.type === 'dropdown-v2') {
      return (
        <div>
          <FormControl sx={{ marginTop: '6px' }}>
            <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={column.header}>
              {column.header}
            </InputLabel>
            <Select
              labelId={column.header}
              key={column.accessorKey}
              label={column.header}
              name={column.accessorKey}
              variant="standard"
              required={column.required}
              disabled={column.disabled || (!!column.parentField && !values[column.parentField])}
              sx={{ width: '360px' }}
              value={values[column.accessorKey] || ''}
              onChange={(e) => setValues(setValueAndClearDependents(columns, values, e.target.name, e.target.value))}
            >
              {getDependentOptions(column, values).map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {column.required && (
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          )}
        </div>
      );
    }
    return null;
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSubmit(values);
  };

  const chunkedColumns = chunkColumns(columns, 3);

  return (
    <Dialog open={open} fullWidth maxWidth="lg">
      <Box sx={{ padding: 3 }}>
        <Typography variant="h6" sx={{ marginBottom: 2 }}>
          Add New Contact
        </Typography>
        
        {/* Google Places Address Search Section */}
        <AddressAutocompleteSection
          values={values}
          setValues={setValues}
          countriesEnum={countriesEnum}
          provincesEnum={provincesEnum}
        />

        {/* Manual Input Fields */}
        <div className={classes.dialogContentContainer}>
          <Stack
            sx={{
              width: '100%',
              minWidth: { xs: '300px', sm: '360px', md: '400px', lg: '400px' },
              gap: '1.5rem',
            }}
          >
            {chunkedColumns.map((columnChunk, rowIndex) => (
              <Grid container item spacing={2} key={rowIndex}>
                {columnChunk.map((column, colIndex) => {
                  if(column.dependentOn && values[column.dependentOn] === column.dependentValue)
                  {
                    return (
                      <Grid item xs={4} key={column.accessorKey}>
                        {renderInput(column, rowIndex * colIndex)}
                      </Grid>
                    );
                  }
                  else {
                    return (
                      <Grid item xs={4} key={column.accessorKey}>
                        {renderInput(column, rowIndex * colIndex)}
                      </Grid>
                    );
                  }
                })}
              </Grid>
            ))}
          </Stack>
        </div>
      </Box>
      {isLoading && (
        <DialogActions sx={{ p: '1.25rem' }}>
          <Spinner />
        </DialogActions>
      )}
      {!isLoading && (
        <DialogActions sx={{ p: '1.25rem' }}>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button color="primary" onClick={handleSubmit} variant="contained" disabled={!isDirty}>
            Create
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export { ContactsEditRecordModal, ContactsCreateRecordModal };

export default compose(
  withStyles(styles)
)(ContactsEditRecordModal);
